import React, { useRef, useState } from 'react'
import { Box, InputBase } from '@mui/material'

interface OtpCodeInputProps {
  length?: number
  value: string
  onChange: (value: string) => void
  hasError?: boolean
  isDarkMode?: boolean
}

const OtpCodeInput: React.FC<OtpCodeInputProps> = ({
  length = 6,
  value,
  onChange,
  hasError = false,
  isDarkMode = false,
}) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const handleChange = (index: number, raw: string) => {
    const digitsOnly = raw.replace(/[^0-9]/g, '')

    if (!digitsOnly) {
      const chars = value.split('')
      chars[index] = ''
      onChange(chars.join(''))
      return
    }

    const chars = value.padEnd(length, ' ').split('')

    for (let i = 0; i < digitsOnly.length && index + i < length; i++) {
      chars[index + i] = digitsOnly[i]
    }

    const newValue = chars.join('').trimEnd()
    onChange(newValue)

    const nextIndex = Math.min(index + digitsOnly.length, length - 1)
    inputsRef.current[nextIndex]?.focus()
  }

  const handlePaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text')
    const digitsOnly = pastedData.replace(/[^0-9]/g, '')
    if (!digitsOnly) return

    // If pasting a full code, start from the beginning; otherwise from current index
    const startIndex = digitsOnly.length >= length ? 0 : index
    const chars = value.padEnd(length, ' ').split('')

    for (let i = 0; i < digitsOnly.length && startIndex + i < length; i++) {
      chars[startIndex + i] = digitsOnly[i]
    }

    const newValue = chars.join('').trimEnd()
    onChange(newValue)

    const nextIndex = Math.min(startIndex + digitsOnly.length, length - 1)
    inputsRef.current[nextIndex]?.focus()
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        inputsRef.current[index - 1]?.focus()
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus()
    }

    if (e.key === 'ArrowRight' && index < length - 1) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  // Theme-aware colors
  const colors = {
    boxBg: isDarkMode ? '#0f1f35' : '#FFFFFF',
    boxBorder: hasError ? '#E53935' : (isDarkMode ? '#1e3a5f' : '#DFE7F0'),
    boxShadow: hasError
      ? '0px 0px 0px 0px #E53935'
      : isDarkMode
        ? '0px 6px 16px rgba(0, 0, 0, 0.3)'
        : '0px 6px 16px rgba(13, 75, 120, 0.10)',
    textColor: isDarkMode ? '#FFFFFF' : '#0B4F6C',
    accentColor: '#7CAD44',
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        justifyContent: 'center',
        mt: 2,
        direction: 'ltr',
      }}
    >
      {Array.from({ length }).map((_, i) => {
        const char = value[i] || ''
        const isActive = activeIndex === i

        return (
          <Box
            key={i}
            sx={{
              width: 64,
              minWidth: 0,
              height: 88,
              borderRadius: '12px',
              border: `1px solid ${colors.boxBorder}`,
              bgcolor: colors.boxBg,
              boxShadow: colors.boxShadow,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
            }}
          >
            <InputBase
              inputRef={el => (inputsRef.current[i] = el)}
              value={char}
              onChange={e => handleChange(i, e.target.value)}
              onPaste={(e: React.ClipboardEvent) => handlePaste(e, i)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                handleKeyDown(e, i)
              }
              onFocus={() => setActiveIndex(i)}
              onBlur={() => setActiveIndex(null)}
              inputProps={{
                maxLength: 1,
                style: { textAlign: 'center' },
              }}
              sx={{
                width: '100%',
                height: '100%',
                fontSize: '1.6rem',
                fontWeight: 600,
                color: colors.textColor,
                '& input': { padding: 0 },
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                height: 13,
                width: '100%',
                bgcolor: colors.accentColor,
                opacity: isActive ? 1 : 0,
                transform: isActive ? 'scaleX(1)' : 'scaleX(0.2)',
                transformOrigin: 'center',
                transition: 'opacity 0.25s ease, transform 0.25s ease',
              }}
            />
          </Box>
        )
      })}
    </Box>
  )
}

export default OtpCodeInput

